import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Camera, X } from 'lucide-react'
import { Html5Qrcode } from 'html5-qrcode'
import { Button } from '@/components/ui/Button'
import { parseContactQrPayload } from '@/utils/parseContactQrPayload'
import type { QrLeadScanResult } from '@/utils/qrLeadScanFlow'

export type ContactQrScanResult = QrLeadScanResult

type Props = {
  open: boolean
  onClose: () => void
  onScan: (result: ContactQrScanResult) => void
}

export function ContactQrScannerModal({ open, onClose, onScan }: Props) {
  const readerId = useId().replace(/:/g, '')
  const scannerRef = useRef<Html5Qrcode | null>(null)
  const handledRef = useRef(false)
  const [error, setError] = useState<string | null>(null)
  const [starting, setStarting] = useState(false)

  const stopScanner = useCallback(async () => {
    const scanner = scannerRef.current
    scannerRef.current = null
    if (!scanner) return
    try {
      if (scanner.isScanning) {
        await scanner.stop()
      }
      scanner.clear()
    } catch {
      // ignore teardown errors
    }
  }, [])

  const handleDecoded = useCallback(
    (raw: string) => {
      if (handledRef.current) return
      const parsed = parseContactQrPayload(raw)
      if (!parsed?.telefone && !parsed?.nome) {
        setError('QR não reconhecido. Use o QR de contato do WhatsApp ou um cartão vCard.')
        return
      }

      handledRef.current = true
      onScan({
        nome: parsed.nome,
        telefone: parsed.telefone,
        source: parsed.source,
      })
      void stopScanner()
      onClose()
    },
    [onClose, onScan, stopScanner],
  )

  useEffect(() => {
    if (!open) {
      handledRef.current = false
      setError(null)
      void stopScanner()
      return
    }

    let cancelled = false

    const start = async () => {
      setStarting(true)
      setError(null)
      handledRef.current = false

      try {
        await stopScanner()
        if (cancelled) return

        const scanner = new Html5Qrcode(readerId, { verbose: false })
        scannerRef.current = scanner

        await scanner.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 260, height: 260 }, aspectRatio: 1 },
          (decoded) => handleDecoded(decoded),
          () => {},
        )
      } catch {
        if (!cancelled) {
          setError(
            'Não foi possível acessar a câmera. Verifique as permissões do navegador e tente novamente.',
          )
        }
      } finally {
        if (!cancelled) setStarting(false)
      }
    }

    void start()

    return () => {
      cancelled = true
      void stopScanner()
    }
  }, [open, readerId, handleDecoded, stopScanner])

  const handleClose = () => {
    void stopScanner()
    onClose()
  }

  if (!open) return null

  return createPortal(
    <div
      className='fixed inset-0 z-[100000] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm'
      onClick={handleClose}
    >
      <div
        className='relative w-full max-w-md overflow-hidden rounded-2xl border border-[color:var(--border)] bg-[var(--bg-card)] shadow-2xl'
        onClick={(e) => e.stopPropagation()}
      >
        <div className='flex items-center justify-between border-b border-[color:var(--border)] px-4 py-3'>
          <div className='flex items-center gap-2'>
            <div className='rounded-lg bg-accent/10 p-2 text-accent'>
              <Camera size={16} />
            </div>
            <div>
              <h3 className='text-sm font-semibold text-[var(--text-primary)]'>Escanear QR do WhatsApp</h3>
              <p className='text-[11px] text-text-muted'>Lê o telefone — nome e empresa você preenche</p>
            </div>
          </div>
          <button
            type='button'
            onClick={handleClose}
            className='rounded-lg border border-[color:var(--border)] p-2 text-text-muted transition-colors hover:text-[var(--text-primary)]'
            aria-label='Fechar'
          >
            <X size={16} />
          </button>
        </div>

        <div className='space-y-3 p-4'>
          <div className='overflow-hidden rounded-xl border border-[color:var(--border)] bg-black'>
            <div id={readerId} className='min-h-[280px] w-full' />
          </div>

          {starting && (
            <p className='text-center text-xs text-text-muted'>Iniciando câmera…</p>
          )}

          {error && (
            <p className='rounded-lg border border-red-500/20 bg-red-500/10 px-3 py-2 text-xs text-red-600 dark:text-red-400'>
              {error}
            </p>
          )}

          <p className='text-[11px] leading-relaxed text-text-muted'>
            Use o QR do perfil do WhatsApp (Meu código). O sistema preenche o{' '}
            <strong className='font-medium text-[var(--text-primary)]'>telefone</strong>; em seguida
            informe o <strong className='font-medium text-[var(--text-primary)]'>nome</strong> e a{' '}
            <strong className='font-medium text-[var(--text-primary)]'>empresa</strong>. Cartões vCard
            podem trazer o nome automaticamente.
          </p>

          <Button type='button' variant='secondary' className='w-full' onClick={handleClose}>
            Cancelar
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
